// Founder P0 2026-09 — TalentMap operational assignment traceability:
// employee, assessment, assigned date, live status, completion date.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import { ClipboardList } from 'lucide-react';

const TONE = {
  requested: 'bg-sky-100 text-sky-800',
  in_progress: 'bg-amber-100 text-amber-800',
  submitted: 'bg-emerald-100 text-emerald-800',
  draft: 'bg-muted text-muted-foreground',
};

export const AssignmentsTraceCard = ({ suite = 'talentmap', refreshKey = 0 }) => {
  const { t, i18n } = useTranslation();
  const isES = (i18n.language || '').startsWith('es');
  const L = (es, en) => (isES ? es : en);
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/client/assignments-overview', { params: { suite } });
      setRows(data || []);
    } catch { /* optional card */ }
  }, [suite]);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (!rows.length) return null;

  const stLabel = (s) => ({
    requested: L('Disponible en Mi Portal', 'Available in Mi Portal'),
    in_progress: L('En curso', 'In progress'),
    submitted: L('Completada', 'Completed'),
    draft: L('Pendiente de iniciar', 'Pending start'),
  }[s] || s);

  return (
    <Card className="p-5 shadow-card" data-testid={`${suite}-assignments-card`}>
      <div className="flex items-center gap-2 mb-3">
        <ClipboardList className="h-5 w-5 text-primary" />
        <h3 className="font-semibold">{L('Evaluaciones asignadas', 'Assigned evaluations')}</h3>
        <span className="text-sm text-muted-foreground">({rows.length})</span>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{L('Colaborador', 'Collaborator')}</TableHead>
              <TableHead>{L('Evaluación', 'Assessment')}</TableHead>
              <TableHead>{L('Asignada', 'Assigned')}</TableHead>
              <TableHead>{L('Estado', 'Status')}</TableHead>
              <TableHead>{L('Completada', 'Completed')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`assign-row-${r.id}`}>
                <TableCell className="font-medium">{r.employee_name || '—'}</TableCell>
                <TableCell className="max-w-[280px] truncate">{r.title || '—'}</TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {r.created_at ? new Date(r.created_at).toLocaleDateString(isES ? 'es' : 'en') : '—'}
                </TableCell>
                <TableCell>
                  <Badge className={`${TONE[r.status] || 'bg-muted'} text-xs uppercase`}>{stLabel(r.status)}</Badge>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString(isES ? 'es' : 'en') : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};

export default AssignmentsTraceCard;
